import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wifi, WifiOff, RefreshCw, AlertTriangle, Check, X, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { syncManager } from '../lib/syncManager';
import { getConflicts, resolveConflict, clearResolvedConflicts, getSyncQueue } from '../lib/offlineStorage';
import { toast } from 'sonner';

const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [conflicts, setConflicts] = useState([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);

  useEffect(() => {
    // Online/offline listeners
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Sync manager listeners
    const unsubscribe = syncManager.subscribe((event, data) => {
      switch (event) {
        case 'syncStart':
          setIsSyncing(true);
          break;
        case 'syncComplete':
          setIsSyncing(false);
          loadPendingCount();
          loadConflicts();
          if (data.synced > 0) {
            toast.success(`${data.synced} Änderung(en) synchronisiert`);
          }
          if (data.conflicts > 0) {
            toast.warning(`${data.conflicts} Konflikt(e) gefunden`);
            setShowConflicts(true);
          }
          break;
        case 'queued':
          loadPendingCount();
          toast.info('Änderung für Synchronisation gespeichert', {
            description: 'Wird synchronisiert sobald online'
          });
          break;
        case 'conflict':
          loadConflicts();
          break;
        default:
          break;
      }
    });

    // Initial load
    loadPendingCount();
    loadConflicts();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      unsubscribe();
    };
  }, []);

  const loadPendingCount = async () => {
    const count = await syncManager.getPendingCount();
    setPendingCount(count);
  };

  const loadConflicts = async () => {
    const c = await getConflicts();
    setConflicts(c.filter(x => !x.resolved));
  };

  const handleManualSync = async () => {
    if (!isOnline) {
      toast.error('Keine Internetverbindung');
      return;
    }
    const result = await syncManager.syncAll();
    if (result.synced === 0 && result.failed === 0 && result.conflicts === 0) {
      toast.info('Alles synchronisiert');
    }
  };

  const handleResolveConflict = async (conflict, keepLocal) => {
    if (keepLocal) {
      // Re-queue the action
      await syncManager.queueAction(conflict.action);
      toast.success('Lokale Änderung wird erneut versucht');
    } else {
      toast.info('Server-Version beibehalten');
    }
    await resolveConflict(conflict.id, keepLocal ? 'local' : 'server');
    loadConflicts();
    setSelectedConflict(null);
  };

  const unresolvedConflicts = conflicts.filter(c => !c.resolved);

  return (
    <>
      {/* Floating Status Indicator */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
        <AnimatePresence>
          {/* Offline Badge */}
          {!isOnline && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              className="bg-amber-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2"
            >
              <WifiOff className="w-4 h-4" />
              <span className="font-medium">Offline-Modus</span>
            </motion.div>
          )}

          {/* Pending Sync Badge */}
          {pendingCount > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              className="bg-blue-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 cursor-pointer"
              onClick={handleManualSync}
            >
              {isSyncing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Clock className="w-4 h-4" />
              )}
              <span className="font-medium">
                {pendingCount} ausstehend
              </span>
              {isOnline && !isSyncing && (
                <RefreshCw className="w-4 h-4 ml-1" />
              )}
            </motion.div>
          )}

          {/* Conflicts Badge */}
          {unresolvedConflicts.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              className="bg-rose-500 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 cursor-pointer"
              onClick={() => setShowConflicts(true)}
            >
              <AlertTriangle className="w-4 h-4" />
              <span className="font-medium">
                {unresolvedConflicts.length} Konflikt(e)
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Conflicts Dialog */}
      <Dialog open={showConflicts} onOpenChange={setShowConflicts}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Synchronisierungs-Konflikte
            </DialogTitle>
          </DialogHeader>
          
          {unresolvedConflicts.length === 0 ? (
            <div className="text-center py-8">
              <Check className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
              <p className="text-slate-500">Keine Konflikte vorhanden</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {unresolvedConflicts.map((conflict) => (
                <div 
                  key={conflict.id}
                  className="border border-amber-200 bg-amber-50 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-amber-800">
                        {conflict.action?.description || 'Unbekannte Aktion'}
                      </div>
                      <div className="text-sm text-amber-600 mt-1">
                        {conflict.type === 'conflict' && 'Daten wurden auf dem Server geändert'}
                        {conflict.type === 'not_found' && 'Eintrag existiert nicht mehr'}
                        {conflict.type === 'failed' && 'Synchronisation fehlgeschlagen'}
                      </div>
                      <div className="text-xs text-slate-500 mt-2">
                        {new Date(conflict.timestamp).toLocaleString('de-DE')}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelectedConflict(conflict)}
                    >
                      Lösen
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Conflict Resolution Dialog */}
      <AlertDialog open={!!selectedConflict} onOpenChange={() => setSelectedConflict(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Konflikt lösen</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="space-y-3">
                <p>
                  <strong>Aktion:</strong> {selectedConflict?.action?.description}
                </p>
                <p>
                  <strong>Problem:</strong>{' '}
                  {selectedConflict?.type === 'conflict' && 'Die Daten wurden in der Zwischenzeit auf dem Server geändert.'}
                  {selectedConflict?.type === 'not_found' && 'Der Eintrag existiert nicht mehr auf dem Server.'}
                  {selectedConflict?.type === 'failed' && 'Die Synchronisation ist mehrfach fehlgeschlagen.'}
                </p>
                <p className="font-medium">Wie möchten Sie fortfahren?</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex gap-2">
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleResolveConflict(selectedConflict, false)}
              className="bg-slate-600 hover:bg-slate-700"
            >
              Server-Version behalten
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() => handleResolveConflict(selectedConflict, true)}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Erneut versuchen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default OfflineIndicator;
